import { PATH } from '@/router/PATH';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type BooksTabId = 'accounts' | 'journal' | 'trial-balance' | 'vat';

export type BooksTab = {
    id: BooksTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
};

/**
 * The four Books tabs, in display order. The first is the default: the bare
 * `/accounting/books` renders it through an index route, so the tab bar reads
 * the same whether the URL carries the `accounts` segment or not.
 */
export const BOOKS_TABS: BooksTab[] = [
    {
        id: 'accounts',
        path: PATH.ACCOUNTING.BOOKS.ACCOUNTS,
        labelKey: 'accounting.books.tabs.accounts',
    },
    {
        id: 'journal',
        path: PATH.ACCOUNTING.BOOKS.JOURNAL,
        labelKey: 'accounting.books.tabs.journal',
    },
    {
        id: 'trial-balance',
        path: PATH.ACCOUNTING.BOOKS.TRIAL_BALANCE,
        labelKey: 'accounting.books.tabs.trialBalance',
    },
    {
        id: 'vat',
        path: PATH.ACCOUNTING.BOOKS.VAT,
        labelKey: 'accounting.books.tabs.vat',
    },
];

export const DEFAULT_BOOKS_TAB_ID: BooksTabId = 'accounts';

/**
 * Which tab the current URL selects. The bare shell path (`/accounting/books`)
 * and anything unrecognised fall back to the default tab, so the bar is never
 * rendered with nothing selected.
 */
export const resolveBooksTabId = (pathname: string): BooksTabId => {
    const matched = BOOKS_TABS.find(
        (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
    );

    return matched?.id ?? DEFAULT_BOOKS_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveBooksTabPath = (id: string): string => {
    const matched = BOOKS_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.ACCOUNTING.BOOKS.ACCOUNTS;
};
